/**
 * Generated by orval v7.10.0 🍺
 * Do not edit manually.
 * Fintecy Investors API
 * API for managing investors, accounts, and transactions in the Fintecy platform
 * OpenAPI spec version: 1.0.0
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';

import type {
  BrokerTransaction,
  CandleSeries,
  Error,
  GetBrokerTransactionsParams,
  GetMarketCandlesParams,
  GetMarketInstrumentsParams,
  GetPortfolioSeriesParams,
  GetPositionPnlParams,
  Holding,
  MarketInstrument,
  MarketQuote,
  Order,
  OrderInput,
  PnlBreakdown,
  PortfolioSnapshot,
  RiskLimits,
  WatchlistItem,
  WatchlistItemInput,
} from '.././model';

import { customInstance } from '../../axios-instance';

/**
 * Returns holdings for an investing-capable account.
 * @summary List holdings for an account
 */
export const getAccountHoldings = (accountId: string, signal?: AbortSignal) => {
  return customInstance<Holding[]>({
    url: `/api/accounts/${accountId}/holdings`,
    method: 'GET',
    signal,
  });
};

export const getGetAccountHoldingsQueryKey = (accountId: string) => {
  return [`/api/accounts/${accountId}/holdings`] as const;
};

export const getGetAccountHoldingsQueryOptions = <
  TData = Awaited<ReturnType<typeof getAccountHoldings>>,
  TError = Error,
>(
  accountId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAccountHoldings>>, TError, TData>>;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAccountHoldingsQueryKey(accountId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAccountHoldings>>> = ({ signal }) =>
    getAccountHoldings(accountId, signal);

  return { queryKey, queryFn, enabled: !!accountId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAccountHoldings>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAccountHoldingsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAccountHoldings>>
>;
export type GetAccountHoldingsQueryError = Error;

export function useGetAccountHoldings<
  TData = Awaited<ReturnType<typeof getAccountHoldings>>,
  TError = Error,
>(
  accountId: string,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAccountHoldings>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAccountHoldings>>,
          TError,
          Awaited<ReturnType<typeof getAccountHoldings>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAccountHoldings<
  TData = Awaited<ReturnType<typeof getAccountHoldings>>,
  TError = Error,
>(
  accountId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getAccountHoldings>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAccountHoldings>>,
          TError,
          Awaited<ReturnType<typeof getAccountHoldings>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAccountHoldings<
  TData = Awaited<ReturnType<typeof getAccountHoldings>>,
  TError = Error,
>(
  accountId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAccountHoldings>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List holdings for an account
 */

export function useGetAccountHoldings<
  TData = Awaited<ReturnType<typeof getAccountHoldings>>,
  TError = Error,
>(
  accountId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAccountHoldings>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAccountHoldingsQueryOptions(accountId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Returns a single holding.
 * @summary Get a holding
 */
export const getAccountHolding = (accountId: string, holdingId: string, signal?: AbortSignal) => {
  return customInstance<Holding>({
    url: `/api/accounts/${accountId}/holdings/${holdingId}`,
    method: 'GET',
    signal,
  });
};

export const getGetAccountHoldingQueryKey = (accountId: string, holdingId: string) => {
  return [`/api/accounts/${accountId}/holdings/${holdingId}`] as const;
};

export const getGetAccountHoldingQueryOptions = <
  TData = Awaited<ReturnType<typeof getAccountHolding>>,
  TError = Error,
>(
  accountId: string,
  holdingId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAccountHolding>>, TError, TData>>;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAccountHoldingQueryKey(accountId, holdingId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAccountHolding>>> = ({ signal }) =>
    getAccountHolding(accountId, holdingId, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!(accountId && holdingId),
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getAccountHolding>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetAccountHoldingQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAccountHolding>>
>;
export type GetAccountHoldingQueryError = Error;

export function useGetAccountHolding<
  TData = Awaited<ReturnType<typeof getAccountHolding>>,
  TError = Error,
>(
  accountId: string,
  holdingId: string,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAccountHolding>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAccountHolding>>,
          TError,
          Awaited<ReturnType<typeof getAccountHolding>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAccountHolding<
  TData = Awaited<ReturnType<typeof getAccountHolding>>,
  TError = Error,
>(
  accountId: string,
  holdingId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAccountHolding>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAccountHolding>>,
          TError,
          Awaited<ReturnType<typeof getAccountHolding>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAccountHolding<
  TData = Awaited<ReturnType<typeof getAccountHolding>>,
  TError = Error,
>(
  accountId: string,
  holdingId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAccountHolding>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get a holding
 */

export function useGetAccountHolding<
  TData = Awaited<ReturnType<typeof getAccountHolding>>,
  TError = Error,
>(
  accountId: string,
  holdingId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAccountHolding>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAccountHoldingQueryOptions(accountId, holdingId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Returns orders for a trading-capable account.
 * @summary List orders for an account
 */
export const getAccountOrders = (accountId: string, signal?: AbortSignal) => {
  return customInstance<Order[]>({
    url: `/api/accounts/${accountId}/orders`,
    method: 'GET',
    signal,
  });
};

export const getGetAccountOrdersQueryKey = (accountId: string) => {
  return [`/api/accounts/${accountId}/orders`] as const;
};

export const getGetAccountOrdersQueryOptions = <
  TData = Awaited<ReturnType<typeof getAccountOrders>>,
  TError = Error,
>(
  accountId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAccountOrders>>, TError, TData>>;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAccountOrdersQueryKey(accountId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAccountOrders>>> = ({ signal }) =>
    getAccountOrders(accountId, signal);

  return { queryKey, queryFn, enabled: !!accountId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAccountOrders>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAccountOrdersQueryResult = NonNullable<Awaited<ReturnType<typeof getAccountOrders>>>;
export type GetAccountOrdersQueryError = Error;

export function useGetAccountOrders<
  TData = Awaited<ReturnType<typeof getAccountOrders>>,
  TError = Error,
>(
  accountId: string,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAccountOrders>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAccountOrders>>,
          TError,
          Awaited<ReturnType<typeof getAccountOrders>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAccountOrders<
  TData = Awaited<ReturnType<typeof getAccountOrders>>,
  TError = Error,
>(
  accountId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAccountOrders>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAccountOrders>>,
          TError,
          Awaited<ReturnType<typeof getAccountOrders>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAccountOrders<
  TData = Awaited<ReturnType<typeof getAccountOrders>>,
  TError = Error,
>(
  accountId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAccountOrders>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List orders for an account
 */

export function useGetAccountOrders<
  TData = Awaited<ReturnType<typeof getAccountOrders>>,
  TError = Error,
>(
  accountId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAccountOrders>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAccountOrdersQueryOptions(accountId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Creates a paper order for a trading-capable account.
 * @summary Place a paper order
 */
export const createAccountOrder = (
  accountId: string,
  orderInput: OrderInput,
  signal?: AbortSignal
) => {
  return customInstance<Order>({
    url: `/api/accounts/${accountId}/orders`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: orderInput,
    signal,
  });
};

export const getCreateAccountOrderMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createAccountOrder>>,
    TError,
    { accountId: string; data: OrderInput },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createAccountOrder>>,
  TError,
  { accountId: string; data: OrderInput },
  TContext
> => {
  const mutationKey = ['createAccountOrder'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createAccountOrder>>,
    { accountId: string; data: OrderInput }
  > = (props) => {
    const { accountId, data } = props ?? {};

    return createAccountOrder(accountId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateAccountOrderMutationResult = NonNullable<
  Awaited<ReturnType<typeof createAccountOrder>>
>;
export type CreateAccountOrderMutationBody = OrderInput;
export type CreateAccountOrderMutationError = Error;

/**
 * @summary Place a paper order
 */
export const useCreateAccountOrder = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createAccountOrder>>,
      TError,
      { accountId: string; data: OrderInput },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createAccountOrder>>,
  TError,
  { accountId: string; data: OrderInput },
  TContext
> => {
  const mutationOptions = getCreateAccountOrderMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Returns a single order.
 * @summary Get an order
 */
export const getAccountOrder = (accountId: string, orderId: string, signal?: AbortSignal) => {
  return customInstance<Order>({
    url: `/api/accounts/${accountId}/orders/${orderId}`,
    method: 'GET',
    signal,
  });
};

export const getGetAccountOrderQueryKey = (accountId: string, orderId: string) => {
  return [`/api/accounts/${accountId}/orders/${orderId}`] as const;
};

export const getGetAccountOrderQueryOptions = <
  TData = Awaited<ReturnType<typeof getAccountOrder>>,
  TError = Error,
>(
  accountId: string,
  orderId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAccountOrder>>, TError, TData>>;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAccountOrderQueryKey(accountId, orderId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAccountOrder>>> = ({ signal }) =>
    getAccountOrder(accountId, orderId, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!(accountId && orderId),
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getAccountOrder>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetAccountOrderQueryResult = NonNullable<Awaited<ReturnType<typeof getAccountOrder>>>;
export type GetAccountOrderQueryError = Error;

export function useGetAccountOrder<
  TData = Awaited<ReturnType<typeof getAccountOrder>>,
  TError = Error,
>(
  accountId: string,
  orderId: string,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAccountOrder>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAccountOrder>>,
          TError,
          Awaited<ReturnType<typeof getAccountOrder>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAccountOrder<
  TData = Awaited<ReturnType<typeof getAccountOrder>>,
  TError = Error,
>(
  accountId: string,
  orderId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAccountOrder>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAccountOrder>>,
          TError,
          Awaited<ReturnType<typeof getAccountOrder>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAccountOrder<
  TData = Awaited<ReturnType<typeof getAccountOrder>>,
  TError = Error,
>(
  accountId: string,
  orderId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAccountOrder>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get an order
 */

export function useGetAccountOrder<
  TData = Awaited<ReturnType<typeof getAccountOrder>>,
  TError = Error,
>(
  accountId: string,
  orderId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAccountOrder>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAccountOrderQueryOptions(accountId, orderId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Cancel a pending order
 */
export const cancelAccountOrder = (accountId: string, orderId: string, signal?: AbortSignal) => {
  return customInstance<Order>({
    url: `/api/accounts/${accountId}/orders/${orderId}/cancel`,
    method: 'POST',
    signal,
  });
};

export const getCancelAccountOrderMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof cancelAccountOrder>>,
    TError,
    { accountId: string; orderId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof cancelAccountOrder>>,
  TError,
  { accountId: string; orderId: string },
  TContext
> => {
  const mutationKey = ['cancelAccountOrder'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof cancelAccountOrder>>,
    { accountId: string; orderId: string }
  > = (props) => {
    const { accountId, orderId } = props ?? {};

    return cancelAccountOrder(accountId, orderId);
  };

  return { mutationFn, ...mutationOptions };
};

export type CancelAccountOrderMutationResult = NonNullable<
  Awaited<ReturnType<typeof cancelAccountOrder>>
>;

export type CancelAccountOrderMutationError = Error;

/**
 * @summary Cancel a pending order
 */
export const useCancelAccountOrder = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof cancelAccountOrder>>,
      TError,
      { accountId: string; orderId: string },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof cancelAccountOrder>>,
  TError,
  { accountId: string; orderId: string },
  TContext
> => {
  const mutationOptions = getCancelAccountOrderMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Per-instrument quote backed by local market data fallback.
 * @summary Get a live quote for an instrument
 */
export const getMarketQuote = (assetId: string, signal?: AbortSignal) => {
  return customInstance<MarketQuote>({
    url: `/api/market/quotes/${assetId}`,
    method: 'GET',
    signal,
  });
};

export const getGetMarketQuoteQueryKey = (assetId: string) => {
  return [`/api/market/quotes/${assetId}`] as const;
};

export const getGetMarketQuoteQueryOptions = <
  TData = Awaited<ReturnType<typeof getMarketQuote>>,
  TError = Error,
>(
  assetId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMarketQuote>>, TError, TData>>;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetMarketQuoteQueryKey(assetId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getMarketQuote>>> = ({ signal }) =>
    getMarketQuote(assetId, signal);

  return { queryKey, queryFn, enabled: !!assetId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getMarketQuote>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetMarketQuoteQueryResult = NonNullable<Awaited<ReturnType<typeof getMarketQuote>>>;
export type GetMarketQuoteQueryError = Error;

export function useGetMarketQuote<
  TData = Awaited<ReturnType<typeof getMarketQuote>>,
  TError = Error,
>(
  assetId: string,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMarketQuote>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMarketQuote>>,
          TError,
          Awaited<ReturnType<typeof getMarketQuote>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetMarketQuote<
  TData = Awaited<ReturnType<typeof getMarketQuote>>,
  TError = Error,
>(
  assetId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMarketQuote>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMarketQuote>>,
          TError,
          Awaited<ReturnType<typeof getMarketQuote>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetMarketQuote<
  TData = Awaited<ReturnType<typeof getMarketQuote>>,
  TError = Error,
>(
  assetId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMarketQuote>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get a live quote for an instrument
 */

export function useGetMarketQuote<
  TData = Awaited<ReturnType<typeof getMarketQuote>>,
  TError = Error,
>(
  assetId: string,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMarketQuote>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetMarketQuoteQueryOptions(assetId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Instrument universe served from the owned catalog (DB-first) with live provider fallback.
 * @summary List tradable instruments with quotes
 */
export const getMarketInstruments = (params?: GetMarketInstrumentsParams, signal?: AbortSignal) => {
  return customInstance<MarketInstrument[]>({
    url: `/api/market/instruments`,
    method: 'GET',
    params,
    signal,
  });
};

export const getGetMarketInstrumentsQueryKey = (params?: GetMarketInstrumentsParams) => {
  return [`/api/market/instruments`, ...(params ? [params] : [])] as const;
};

export const getGetMarketInstrumentsQueryOptions = <
  TData = Awaited<ReturnType<typeof getMarketInstruments>>,
  TError = Error,
>(
  params?: GetMarketInstrumentsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getMarketInstruments>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetMarketInstrumentsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getMarketInstruments>>> = ({ signal }) =>
    getMarketInstruments(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getMarketInstruments>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetMarketInstrumentsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getMarketInstruments>>
>;
export type GetMarketInstrumentsQueryError = Error;

export function useGetMarketInstruments<
  TData = Awaited<ReturnType<typeof getMarketInstruments>>,
  TError = Error,
>(
  params: undefined | GetMarketInstrumentsParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getMarketInstruments>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMarketInstruments>>,
          TError,
          Awaited<ReturnType<typeof getMarketInstruments>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetMarketInstruments<
  TData = Awaited<ReturnType<typeof getMarketInstruments>>,
  TError = Error,
>(
  params?: GetMarketInstrumentsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getMarketInstruments>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMarketInstruments>>,
          TError,
          Awaited<ReturnType<typeof getMarketInstruments>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetMarketInstruments<
  TData = Awaited<ReturnType<typeof getMarketInstruments>>,
  TError = Error,
>(
  params?: GetMarketInstrumentsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getMarketInstruments>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List tradable instruments with quotes
 */

export function useGetMarketInstruments<
  TData = Awaited<ReturnType<typeof getMarketInstruments>>,
  TError = Error,
>(
  params?: GetMarketInstrumentsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getMarketInstruments>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetMarketInstrumentsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Served from the owned candle store (DB-first) with live broker fallback and write-through.
 * @summary Historical OHLCV candles for an instrument
 */
export const getMarketCandles = (
  assetId: string,
  params?: GetMarketCandlesParams,
  signal?: AbortSignal
) => {
  return customInstance<CandleSeries>({
    url: `/api/market/candles/${assetId}`,
    method: 'GET',
    params,
    signal,
  });
};

export const getGetMarketCandlesQueryKey = (assetId: string, params?: GetMarketCandlesParams) => {
  return [`/api/market/candles/${assetId}`, ...(params ? [params] : [])] as const;
};

export const getGetMarketCandlesQueryOptions = <
  TData = Awaited<ReturnType<typeof getMarketCandles>>,
  TError = Error,
>(
  assetId: string,
  params?: GetMarketCandlesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMarketCandles>>, TError, TData>>;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetMarketCandlesQueryKey(assetId, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getMarketCandles>>> = ({ signal }) =>
    getMarketCandles(assetId, params, signal);

  return { queryKey, queryFn, enabled: !!assetId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getMarketCandles>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetMarketCandlesQueryResult = NonNullable<Awaited<ReturnType<typeof getMarketCandles>>>;
export type GetMarketCandlesQueryError = Error;

export function useGetMarketCandles<
  TData = Awaited<ReturnType<typeof getMarketCandles>>,
  TError = Error,
>(
  assetId: string,
  params: undefined | GetMarketCandlesParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMarketCandles>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMarketCandles>>,
          TError,
          Awaited<ReturnType<typeof getMarketCandles>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetMarketCandles<
  TData = Awaited<ReturnType<typeof getMarketCandles>>,
  TError = Error,
>(
  assetId: string,
  params?: GetMarketCandlesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMarketCandles>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMarketCandles>>,
          TError,
          Awaited<ReturnType<typeof getMarketCandles>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetMarketCandles<
  TData = Awaited<ReturnType<typeof getMarketCandles>>,
  TError = Error,
>(
  assetId: string,
  params?: GetMarketCandlesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMarketCandles>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Historical OHLCV candles for an instrument
 */

export function useGetMarketCandles<
  TData = Awaited<ReturnType<typeof getMarketCandles>>,
  TError = Error,
>(
  assetId: string,
  params?: GetMarketCandlesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getMarketCandles>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetMarketCandlesQueryOptions(assetId, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Daily snapshots aggregated across the user's trading accounts (or one account).
 * @summary Portfolio value and PnL time series
 */
export const getPortfolioSeries = (params?: GetPortfolioSeriesParams, signal?: AbortSignal) => {
  return customInstance<PortfolioSnapshot[]>({
    url: `/api/trading/analytics/portfolio-series`,
    method: 'GET',
    params,
    signal,
  });
};

export const getGetPortfolioSeriesQueryKey = (params?: GetPortfolioSeriesParams) => {
  return [`/api/trading/analytics/portfolio-series`, ...(params ? [params] : [])] as const;
};

export const getGetPortfolioSeriesQueryOptions = <
  TData = Awaited<ReturnType<typeof getPortfolioSeries>>,
  TError = Error,
>(
  params?: GetPortfolioSeriesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPortfolioSeries>>, TError, TData>>;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPortfolioSeriesQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getPortfolioSeries>>> = ({ signal }) =>
    getPortfolioSeries(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getPortfolioSeries>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetPortfolioSeriesQueryResult = NonNullable<
  Awaited<ReturnType<typeof getPortfolioSeries>>
>;
export type GetPortfolioSeriesQueryError = Error;

export function useGetPortfolioSeries<
  TData = Awaited<ReturnType<typeof getPortfolioSeries>>,
  TError = Error,
>(
  params: undefined | GetPortfolioSeriesParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPortfolioSeries>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPortfolioSeries>>,
          TError,
          Awaited<ReturnType<typeof getPortfolioSeries>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPortfolioSeries<
  TData = Awaited<ReturnType<typeof getPortfolioSeries>>,
  TError = Error,
>(
  params?: GetPortfolioSeriesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getPortfolioSeries>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPortfolioSeries>>,
          TError,
          Awaited<ReturnType<typeof getPortfolioSeries>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPortfolioSeries<
  TData = Awaited<ReturnType<typeof getPortfolioSeries>>,
  TError = Error,
>(
  params?: GetPortfolioSeriesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPortfolioSeries>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Portfolio value and PnL time series
 */

export function useGetPortfolioSeries<
  TData = Awaited<ReturnType<typeof getPortfolioSeries>>,
  TError = Error,
>(
  params?: GetPortfolioSeriesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPortfolioSeries>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetPortfolioSeriesQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Realized/unrealized PnL breakdown for one position
 */
export const getPositionPnl = (
  assetId: string,
  params: GetPositionPnlParams,
  signal?: AbortSignal
) => {
  return customInstance<PnlBreakdown>({
    url: `/api/trading/positions/${assetId}/pnl`,
    method: 'GET',
    params,
    signal,
  });
};

export const getGetPositionPnlQueryKey = (assetId: string, params: GetPositionPnlParams) => {
  return [`/api/trading/positions/${assetId}/pnl`, ...(params ? [params] : [])] as const;
};

export const getGetPositionPnlQueryOptions = <
  TData = Awaited<ReturnType<typeof getPositionPnl>>,
  TError = Error,
>(
  assetId: string,
  params: GetPositionPnlParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPositionPnl>>, TError, TData>>;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetPositionPnlQueryKey(assetId, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getPositionPnl>>> = ({ signal }) =>
    getPositionPnl(assetId, params, signal);

  return { queryKey, queryFn, enabled: !!assetId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getPositionPnl>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetPositionPnlQueryResult = NonNullable<Awaited<ReturnType<typeof getPositionPnl>>>;
export type GetPositionPnlQueryError = Error;

export function useGetPositionPnl<
  TData = Awaited<ReturnType<typeof getPositionPnl>>,
  TError = Error,
>(
  assetId: string,
  params: GetPositionPnlParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPositionPnl>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPositionPnl>>,
          TError,
          Awaited<ReturnType<typeof getPositionPnl>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPositionPnl<
  TData = Awaited<ReturnType<typeof getPositionPnl>>,
  TError = Error,
>(
  assetId: string,
  params: GetPositionPnlParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPositionPnl>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPositionPnl>>,
          TError,
          Awaited<ReturnType<typeof getPositionPnl>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetPositionPnl<
  TData = Awaited<ReturnType<typeof getPositionPnl>>,
  TError = Error,
>(
  assetId: string,
  params: GetPositionPnlParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPositionPnl>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Realized/unrealized PnL breakdown for one position
 */

export function useGetPositionPnl<
  TData = Awaited<ReturnType<typeof getPositionPnl>>,
  TError = Error,
>(
  assetId: string,
  params: GetPositionPnlParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getPositionPnl>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetPositionPnlQueryOptions(assetId, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Pre-trade risk limits
 */
export const getRiskLimits = (signal?: AbortSignal) => {
  return customInstance<RiskLimits>({ url: `/api/trading/risk-limits`, method: 'GET', signal });
};

export const getGetRiskLimitsQueryKey = () => {
  return [`/api/trading/risk-limits`] as const;
};

export const getGetRiskLimitsQueryOptions = <
  TData = Awaited<ReturnType<typeof getRiskLimits>>,
  TError = unknown,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRiskLimits>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetRiskLimitsQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getRiskLimits>>> = ({ signal }) =>
    getRiskLimits(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getRiskLimits>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetRiskLimitsQueryResult = NonNullable<Awaited<ReturnType<typeof getRiskLimits>>>;
export type GetRiskLimitsQueryError = unknown;

export function useGetRiskLimits<
  TData = Awaited<ReturnType<typeof getRiskLimits>>,
  TError = unknown,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRiskLimits>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getRiskLimits>>,
          TError,
          Awaited<ReturnType<typeof getRiskLimits>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetRiskLimits<
  TData = Awaited<ReturnType<typeof getRiskLimits>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRiskLimits>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getRiskLimits>>,
          TError,
          Awaited<ReturnType<typeof getRiskLimits>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetRiskLimits<
  TData = Awaited<ReturnType<typeof getRiskLimits>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRiskLimits>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Pre-trade risk limits
 */

export function useGetRiskLimits<
  TData = Awaited<ReturnType<typeof getRiskLimits>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getRiskLimits>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetRiskLimitsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Update pre-trade risk limits
 */
export const updateRiskLimits = (riskLimits: RiskLimits) => {
  return customInstance<RiskLimits>({
    url: `/api/trading/risk-limits`,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    data: riskLimits,
  });
};

export const getUpdateRiskLimitsMutationOptions = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateRiskLimits>>,
    TError,
    { data: RiskLimits },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateRiskLimits>>,
  TError,
  { data: RiskLimits },
  TContext
> => {
  const mutationKey = ['updateRiskLimits'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateRiskLimits>>,
    { data: RiskLimits }
  > = (props) => {
    const { data } = props ?? {};

    return updateRiskLimits(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateRiskLimitsMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateRiskLimits>>
>;
export type UpdateRiskLimitsMutationBody = RiskLimits;
export type UpdateRiskLimitsMutationError = unknown;

/**
 * @summary Update pre-trade risk limits
 */
export const useUpdateRiskLimits = <TError = unknown, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateRiskLimits>>,
      TError,
      { data: RiskLimits },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof updateRiskLimits>>,
  TError,
  { data: RiskLimits },
  TContext
> => {
  const mutationOptions = getUpdateRiskLimitsMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * @summary Imported broker transaction history
 */
export const getBrokerTransactions = (
  params?: GetBrokerTransactionsParams,
  signal?: AbortSignal
) => {
  return customInstance<BrokerTransaction[]>({
    url: `/api/trading/transactions`,
    method: 'GET',
    params,
    signal,
  });
};

export const getGetBrokerTransactionsQueryKey = (params?: GetBrokerTransactionsParams) => {
  return [`/api/trading/transactions`, ...(params ? [params] : [])] as const;
};

export const getGetBrokerTransactionsQueryOptions = <
  TData = Awaited<ReturnType<typeof getBrokerTransactions>>,
  TError = Error,
>(
  params?: GetBrokerTransactionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBrokerTransactions>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetBrokerTransactionsQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getBrokerTransactions>>> = ({ signal }) =>
    getBrokerTransactions(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getBrokerTransactions>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetBrokerTransactionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getBrokerTransactions>>
>;
export type GetBrokerTransactionsQueryError = Error;

export function useGetBrokerTransactions<
  TData = Awaited<ReturnType<typeof getBrokerTransactions>>,
  TError = Error,
>(
  params: undefined | GetBrokerTransactionsParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBrokerTransactions>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBrokerTransactions>>,
          TError,
          Awaited<ReturnType<typeof getBrokerTransactions>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetBrokerTransactions<
  TData = Awaited<ReturnType<typeof getBrokerTransactions>>,
  TError = Error,
>(
  params?: GetBrokerTransactionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBrokerTransactions>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBrokerTransactions>>,
          TError,
          Awaited<ReturnType<typeof getBrokerTransactions>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetBrokerTransactions<
  TData = Awaited<ReturnType<typeof getBrokerTransactions>>,
  TError = Error,
>(
  params?: GetBrokerTransactionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBrokerTransactions>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Imported broker transaction history
 */

export function useGetBrokerTransactions<
  TData = Awaited<ReturnType<typeof getBrokerTransactions>>,
  TError = Error,
>(
  params?: GetBrokerTransactionsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getBrokerTransactions>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetBrokerTransactionsQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary List watchlist items
 */
export const getWatchlist = (signal?: AbortSignal) => {
  return customInstance<WatchlistItem[]>({ url: `/api/watchlist`, method: 'GET', signal });
};

export const getGetWatchlistQueryKey = () => {
  return [`/api/watchlist`] as const;
};

export const getGetWatchlistQueryOptions = <
  TData = Awaited<ReturnType<typeof getWatchlist>>,
  TError = unknown,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWatchlist>>, TError, TData>>;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetWatchlistQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getWatchlist>>> = ({ signal }) =>
    getWatchlist(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getWatchlist>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetWatchlistQueryResult = NonNullable<Awaited<ReturnType<typeof getWatchlist>>>;
export type GetWatchlistQueryError = unknown;

export function useGetWatchlist<TData = Awaited<ReturnType<typeof getWatchlist>>, TError = unknown>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWatchlist>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWatchlist>>,
          TError,
          Awaited<ReturnType<typeof getWatchlist>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetWatchlist<TData = Awaited<ReturnType<typeof getWatchlist>>, TError = unknown>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWatchlist>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWatchlist>>,
          TError,
          Awaited<ReturnType<typeof getWatchlist>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetWatchlist<TData = Awaited<ReturnType<typeof getWatchlist>>, TError = unknown>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWatchlist>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List watchlist items
 */

export function useGetWatchlist<TData = Awaited<ReturnType<typeof getWatchlist>>, TError = unknown>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWatchlist>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetWatchlistQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Add asset to watchlist
 */
export const addWatchlistItem = (watchlistItemInput: WatchlistItemInput, signal?: AbortSignal) => {
  return customInstance<WatchlistItem>({
    url: `/api/watchlist`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: watchlistItemInput,
    signal,
  });
};

export const getAddWatchlistItemMutationOptions = <TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof addWatchlistItem>>,
    TError,
    { data: WatchlistItemInput },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof addWatchlistItem>>,
  TError,
  { data: WatchlistItemInput },
  TContext
> => {
  const mutationKey = ['addWatchlistItem'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof addWatchlistItem>>,
    { data: WatchlistItemInput }
  > = (props) => {
    const { data } = props ?? {};

    return addWatchlistItem(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type AddWatchlistItemMutationResult = NonNullable<
  Awaited<ReturnType<typeof addWatchlistItem>>
>;
export type AddWatchlistItemMutationBody = WatchlistItemInput;
export type AddWatchlistItemMutationError = unknown;

/**
 * @summary Add asset to watchlist
 */
export const useAddWatchlistItem = <TError = unknown, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof addWatchlistItem>>,
      TError,
      { data: WatchlistItemInput },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof addWatchlistItem>>,
  TError,
  { data: WatchlistItemInput },
  TContext
> => {
  const mutationOptions = getAddWatchlistItemMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * @summary Remove asset from watchlist
 */
export const removeWatchlistItem = (itemId: string) => {
  return customInstance<void>({ url: `/api/watchlist/${itemId}`, method: 'DELETE' });
};

export const getRemoveWatchlistItemMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof removeWatchlistItem>>,
    TError,
    { itemId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof removeWatchlistItem>>,
  TError,
  { itemId: string },
  TContext
> => {
  const mutationKey = ['removeWatchlistItem'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof removeWatchlistItem>>,
    { itemId: string }
  > = (props) => {
    const { itemId } = props ?? {};

    return removeWatchlistItem(itemId);
  };

  return { mutationFn, ...mutationOptions };
};

export type RemoveWatchlistItemMutationResult = NonNullable<
  Awaited<ReturnType<typeof removeWatchlistItem>>
>;

export type RemoveWatchlistItemMutationError = unknown;

/**
 * @summary Remove asset from watchlist
 */
export const useRemoveWatchlistItem = <TError = unknown, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof removeWatchlistItem>>,
      TError,
      { itemId: string },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof removeWatchlistItem>>,
  TError,
  { itemId: string },
  TContext
> => {
  const mutationOptions = getRemoveWatchlistItemMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
